import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MessagingWorkspacePage.css";
import "./components/MessagingButtons.css";
import { useAuth } from "../../hooks/useAuth";
import {
  useMessagingAudience,
  MessagingAudience,
  MessagingAudienceRow,
} from "../../hooks/useMessagingAudience";
import { useMessageTemplates } from "../../hooks/useMessageTemplates";
import { useEntitlements } from "../../hooks/useEntitlements";
import { sendMessageAsync, createCampaignAsync } from "../../api/MessageApi";
import { Constants } from "../../constants/constants";
import {
  ComposeState,
  DEFAULT_COMPOSE,
  MessageChannel,
  MessageLogStatus,
  MessageType,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_TYPE_LABELS,
  normalizeComposeChannel,
  SendCampaignResult,
  SendMessageResult,
} from "../../models/MessageModels";
import { AUDIENCE_LABELS } from "./components/ClientAudienceFilter";
import ClientSelectableTable from "./components/ClientSelectableTable";
import ComposeModal from "./components/ComposeModal";
import ConfirmSendModal from "./components/ConfirmSendModal";
import CampaignResultPanel from "./components/CampaignResultPanel";
import MessageStatusBadge from "./components/MessageStatusBadge";
import WhatsAppOpenButton from "./components/WhatsAppOpenButton";
import Toast from "../../components/Toast";
import MessagingUsageBar from "../../components/MessagingUsageBar";
import { OrgForms } from "../../config/forms";

const MessagingWorkspacePage = () => {
  const { user, hasPrivilege } = useAuth();
  const canSendMessages = hasPrivilege(OrgForms.Messaging, "canCreate");
  const { entitlements, refresh: refreshEntitlements } = useEntitlements();
  const organizationId = user?.activeOrganization?.organizationId;
  const orgName = user?.activeOrganization?.name ?? "";
  const navigate = useNavigate();

  const [audience, setAudience] = useState<MessagingAudience>("all");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const {
    rows,
    pagination,
    loading: audienceLoading,
    error,
    reload,
    setPage,
  } = useMessagingAudience(organizationId, audience, {
    search: appliedSearch,
    dateFrom,
    dateTo,
  });

  const {
    templates,
    loading: templatesLoading,
    load: loadTemplates,
  } = useMessageTemplates(organizationId, true);

  const [selected, setSelected] = useState<Map<string, MessagingAudienceRow>>(
    new Map(),
  );
  const [compose, setCompose] = useState<ComposeState>(DEFAULT_COMPOSE);
  const [composeOpen, setComposeOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentStatus, setSentStatus] = useState<Record<string, number>>({});
  const [singleResult, setSingleResult] = useState<{
    row: MessagingAudienceRow;
    result: SendMessageResult;
  } | null>(null);
  const [campaignResult, setCampaignResult] =
    useState<SendCampaignResult | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const showToast = (message: string, type: "success" | "error" | "info") =>
    setToast({ message, type });

  const changeAudience = (next: MessagingAudience) => {
    setAudience(next);
    setPage(1);
  };

  const submitSearch = () => {
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  const changeDateFrom = (value: string) => {
    setDateFrom(value);
    setPage(1);
  };

  const changeDateTo = (value: string) => {
    setDateTo(value);
    setPage(1);
  };

  const resetFilters = () => {
    setAudience("all");
    setSearchInput("");
    setAppliedSearch("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const selectedRows = useMemo(() => [...selected.values()], [selected]);
  const selectedClientIds = useMemo(() => new Set(selected.keys()), [selected]);

  const hasEnoughRemaining = (count: number): boolean => {
    if (!entitlements || entitlements.monthlyMessageLimit < 0) return true;
    return entitlements.messagesRemaining >= count;
  };

  const limitReached =
    selectedRows.length > 0 && !hasEnoughRemaining(selectedRows.length);
  const remainingNote =
    entitlements && entitlements.monthlyMessageLimit < 0
      ? "Your plan has an unlimited monthly message allowance."
      : `Your plan allows ${entitlements?.messagesRemaining ?? 0} more message${
          entitlements?.messagesRemaining === 1 ? "" : "s"
        } this month.`;
  const dueAtById = useMemo(
    () =>
      new Map(
        rows
          .filter((row) => row.dueAt)
          .map((row) => [row.id, row.dueAt ?? null]),
      ),
    [rows],
  );

  const previewClient = selectedRows[0]
    ? { fullName: selectedRows[0].fullName, phone: selectedRows[0].phone }
    : rows[0]
      ? { fullName: rows[0].fullName, phone: rows[0].phone }
      : null;

  const toggle = (clientId: string) => {
    const row = rows.find((r) => r.clientId === clientId);
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(clientId)) next.delete(clientId);
      else if (row) next.set(clientId, row);
      return next;
    });
  };

  const togglePage = (clientIds: string[]) => {
    setSelected((prev) => {
      const next = new Map(prev);
      clientIds.forEach((clientId) => {
        if (next.has(clientId)) {
          next.delete(clientId);
          return;
        }
        const row = rows.find((r) => r.clientId === clientId);
        if (row) next.set(clientId, row);
      });
      return next;
    });
  };

  const clearSelection = () => setSelected(new Map());

  const buildSingleRequest = (row: MessagingAudienceRow) => ({
    organizationId: organizationId as string,
    clientId: row.clientId,
    channel: normalizeComposeChannel(compose.channel),
    messageType: compose.messageType,
    templateId: compose.templateId,
    body: compose.body || null,
    relatedFollowUpId:
      compose.messageType === MessageType.FollowUp
        ? (row.followUpId ?? null)
        : null,
    relatedAppointmentId: null,
  });

  const buildCampaignRequest = () => {
    const typeLabel =
      MESSAGE_TYPE_LABELS[compose.messageType as MessageType] ?? "Message";
    return {
      organizationId: organizationId as string,
      channel: normalizeComposeChannel(compose.channel),
      messageType: compose.messageType,
      templateId: compose.templateId,
      name: `${typeLabel} — ${new Date().toLocaleString()}`,
      body: compose.body || null,
      clientIds: selectedRows.map((row) => row.clientId),
    };
  };

  const handleSendClick = () => {
    if (selected.size === 0) return;
    if (templates.length === 0 && !templatesLoading) loadTemplates();
    setComposeOpen(true);
  };

  const handleComposerSend = () => {
    setComposeOpen(false);
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    if (!organizationId || selectedRows.length === 0) return;
    if (!hasEnoughRemaining(selectedRows.length)) {
      showToast(
        `Not enough message credit — you need ${selectedRows.length}, only ${entitlements?.messagesRemaining ?? 0} remaining this month.`,
        "error",
      );
      return;
    }
    setConfirmOpen(false);
    setSending(true);
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

    try {
      if (selectedRows.length === 1) {
        const row = selectedRows[0];
        const res = await sendMessageAsync(token, buildSingleRequest(row));
        if (res.success && res.result) {
          const result = res.result;
          await refreshEntitlements();
          setSentStatus((prev) => ({ ...prev, [row.clientId]: result.status }));
          setSingleResult({ row, result });
          if (result.status === MessageLogStatus.Sent) {
            showToast("Message sent", "success");
          } else {
            showToast(result.errorMessage || "Message failed to send", "error");
          }
          if (compose.messageType === MessageType.FollowUp) {
            await reload();
          }
        } else {
          showToast(res.message || "Failed to send message", "error");
        }
      } else {
        const res = await createCampaignAsync(token, buildCampaignRequest());
        if (res.success && res.result) {
          const result = res.result;
          await refreshEntitlements();
          setCampaignResult(result);
          const clientIds = selectedRows.map((row) => row.clientId);
          const statusMap: Record<string, number> = {};
          result.items.forEach((item, index) => {
            if (clientIds[index]) statusMap[clientIds[index]] = item.status;
          });
          setSentStatus((prev) => ({ ...prev, ...statusMap }));
          showToast(
            `Campaign sent — ${result.successCount} sent, ${result.failCount} failed`,
            "success",
          );
        } else {
          showToast(res.message || "Failed to send campaign", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleCreateCampaign = () => {
    if (selectedRows.length === 0) return;
    const params = new URLSearchParams();
    params.set(
      "clientIds",
      selectedRows.map((row) => row.clientId).join(","),
    );
    params.set("channel", String(normalizeComposeChannel(compose.channel)));
    params.set("messageType", String(compose.messageType));
    if (compose.templateId) params.set("templateId", compose.templateId);
    if (compose.body) params.set("body", compose.body);
    setComposeOpen(false);
    navigate(`/messaging/campaigns/new?${params.toString()}`);
  };

  const channelLabel =
    MESSAGE_CHANNEL_LABELS[
      normalizeComposeChannel(compose.channel) as MessageChannel
    ] ?? "Message";

  const loading = audienceLoading;

  return (
    <div className="msg-workspace">
      <div className="msg-workspace__header">
        <div>
          <h2>Messaging</h2>
          <p>
            Select clients to message at {orgName || "your organization"}.
          </p>
        </div>
      </div>

      <MessagingUsageBar entitlements={entitlements} />

      <form
        className="msg-workspace__toolbar"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
      >
        <label className="msg-workspace__field msg-workspace__field--search">
          <span>Search clients</span>
          <input
            type="search"
            placeholder="Name or phone…"
            value={searchInput}
            disabled={loading}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        <button
          type="submit"
          className="msg-btn msg-btn--secondary msg-workspace__search-btn"
          disabled={loading}
        >
          Search
        </button>

        <label className="msg-workspace__field">
          <span>Audience</span>
          <select
            value={audience}
            disabled={loading}
            onChange={(e) => changeAudience(e.target.value as MessagingAudience)}
          >
            {(Object.keys(AUDIENCE_LABELS) as MessagingAudience[]).map((key) => (
              <option key={key} value={key}>
                {AUDIENCE_LABELS[key]}
              </option>
            ))}
          </select>
        </label>

        <label className="msg-workspace__field">
          <span>From</span>
          <input
            type="date"
            value={dateFrom}
            disabled={loading || audience === "all"}
            title={
              audience === "all"
                ? "Date range filters by follow-up due date or appointment date — pick an audience to use it"
                : audience === "appointments"
                  ? "Only clients with an appointment on/after this date"
                  : "Only clients with a follow-up due on/after this date"
            }
            onChange={(e) => changeDateFrom(e.target.value)}
          />
        </label>

        <label className="msg-workspace__field">
          <span>To</span>
          <input
            type="date"
            value={dateTo}
            disabled={loading || audience === "all"}
            title={
              audience === "all"
                ? "Date range filters by follow-up due date or appointment date — pick an audience to use it"
                : audience === "appointments"
                  ? "Only clients with an appointment on/before this date"
                  : "Only clients with a follow-up due on/before this date"
            }
            onChange={(e) => changeDateTo(e.target.value)}
          />
        </label>

        <button
          type="button"
          className="msg-btn msg-btn--secondary msg-workspace__reset"
          onClick={resetFilters}
          disabled={loading}
        >
          Reset
        </button>

        <div className="msg-workspace__toolbar-spacer" />

        <button
          type="button"
          className="msg-btn msg-btn--primary msg-workspace__send"
          disabled={
            selected.size === 0 || loading || limitReached || !canSendMessages
          }
          title={
            !canSendMessages
              ? "You don't have permission to send messages"
              : limitReached
                ? "Not enough monthly message credit for this selection"
                : selected.size === 0
                  ? "Select at least one client first"
                  : "Compose a message to the selected clients"
          }
          onClick={handleSendClick}
        >
          Send{selected.size > 0 ? ` (${selected.size})` : ""}
        </button>
      </form>

      {limitReached && (
        <p className="msg-workspace__limit" role="alert">
          Not enough message credit for {selectedRows.length} client
          {selectedRows.length === 1 ? "" : "s"} — {remainingNote}
        </p>
      )}

      {audience !== "all" && (
        <p className="msg-workspace__hint">
          From / To filters narrow by{" "}
          {audience === "appointments"
            ? "appointment date"
            : "follow-up due date"}
          .
        </p>
      )}

      <section className="msg-workspace__table">
        <ClientSelectableTable
          rows={rows}
          totalCount={pagination.totalCount}
          page={pagination.page}
          pageSize={pagination.pageSize}
          onPageChange={setPage}
          selectedIds={selectedClientIds}
          sentStatus={sentStatus}
          dueAtById={dueAtById}
          loading={loading}
          error={error}
          onRetry={reload}
          onToggle={toggle}
          onSelectPage={togglePage}
          onClearSelection={clearSelection}
        />

        {singleResult && (
          <div className="msg-single-result">
            <div className="msg-single-result__meta">
              <span className="msg-single-result__name">
                {singleResult.row.fullName}
              </span>
              <MessageStatusBadge status={singleResult.result.status} />
            </div>
            {singleResult.result.whatsAppUrl && (
              <WhatsAppOpenButton url={singleResult.result.whatsAppUrl} />
            )}
            {singleResult.result.errorMessage && (
              <span className="msg-single-result__error">
                {singleResult.result.errorMessage}
              </span>
            )}
            <button
              className="msg-btn msg-btn--link"
              onClick={() => setSingleResult(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {campaignResult && (
          <CampaignResultPanel
            result={campaignResult}
            onViewCampaign={(id) => navigate(`/messaging/campaigns/${id}`)}
          />
        )}
      </section>

      <ComposeModal
        isOpen={composeOpen}
        compose={compose}
        templates={templates}
        orgName={orgName}
        previewClient={previewClient}
        selectedCount={selected.size}
        sending={sending}
        onChange={setCompose}
        onSend={handleComposerSend}
        onCreateCampaign={handleCreateCampaign}
        onClose={() => setComposeOpen(false)}
      />

      <ConfirmSendModal
        isOpen={confirmOpen}
        count={selected.size}
        channelLabel={channelLabel}
        submitting={sending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSend}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default MessagingWorkspacePage;
